import { NgTemplateOutlet } from "@angular/common";
import { Component, input, output } from "@angular/core";
import { BadgeModule } from "primeng/badge";
import { PhotoCaptureButtonComponent } from "../photo-capture-button/photo-capture-button.component";

export interface IconAction {
  /** PrimeIcons class(es), e.g. `pi pi-comment`. Ignored when `capture` is set. */
  icon?: string;
  /** Always required: these controls only ever render a glyph. */
  ariaLabel: string;
  /** Rendered as a small badge on the icon; hidden when zero or omitted. */
  count?: number;
  disabled?: boolean;
  /** Click handler. Without one the icon renders as a static, non-interactive glyph. */
  action?: () => void;
  /** Renders the shared photo capture control instead of a plain icon. */
  capture?: "camera" | "library";
  /** Receives the files chosen by a `capture` action. */
  filesSelected?: (files: File[]) => void;
  /** Id for the hidden file input behind a `capture` action. */
  inputId?: string;
  testId?: string;
}

/**
 * Standardized row of icon-sized actions, e.g. the trailing controls on an answered checklist item.
 *
 * Owns the box size, icon size and badge placement so every caller lines up, and each entry decides
 * whether it is a static glyph, a button, or a photo capture control.
 */
@Component({
  selector: "app-icon-action-group",
  standalone: true,
  imports: [NgTemplateOutlet, BadgeModule, PhotoCaptureButtonComponent],
  templateUrl: "./icon-action-group.component.html",
  styleUrl: "./icon-action-group.component.scss"
})
export class IconActionGroupComponent {
  /** Actions rendered left to right; the array length sets the count. */
  actions = input<IconAction[]>([]);
  testId = input<string>("app-icon-action-group");

  /** Emitted with the clicked action (in addition to running its `action`). */
  readonly actionClick = output<IconAction>();

  protected resolveTestId(action: IconAction, index: number): string {
    return action.testId || `${this.testId()}-${index}`;
  }

  protected handleClick(action: IconAction): void {
    if (action.disabled) {
      return;
    }
    action.action?.();
    this.actionClick.emit(action);
  }
}
